import { useEffect, useMemo, useRef } from "react";

// User-supplied HTML runs in an opaque-origin sandbox (no allow-same-origin), so it can't reach the
// app's storage or tokens. This CSP additionally blocks every network fetch from the document, so
// embedded scripts can't phone home, beacon viewers, or exfiltrate data with credentials baked into
// the file. Inline scripts/styles and data:/blob: resources still work; anything loaded from a URL
// (CDN scripts, web fonts, hotlinked images, fetch/XHR/WebSocket) is blocked.
export const HTML_VIEWER_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline' 'unsafe-eval' 'wasm-unsafe-eval' data: blob:",
  "style-src 'unsafe-inline' data: blob:",
  "img-src data: blob:",
  "font-src data: blob:",
  "media-src data: blob:",
  "connect-src data: blob:",
  "worker-src blob:",
  "frame-src 'none'",
  "object-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
  // WebRTC ICE traffic isn't a fetch, so connect-src doesn't cover it. No current engine enforces this
  // directive (verified in Chromium, Firefox and WebKit), hence also WEBRTC_GUARD below.
  "webrtc 'block'",
].join("; ");

// Removes the WebRTC constructors before any author script runs, so the document can't send STUN/TURN
// traffic, which no CSP directive blocks in practice. Non-configurable, so author script can't restore them.
const WEBRTC_GUARD =
  '<script>for (const name of ["RTCPeerConnection", "webkitRTCPeerConnection"]) if (name in window) ' +
  "Object.defineProperty(window, name, { value: undefined, writable: false, configurable: false });</script>";

const OPEN_LINK_MESSAGE = "istari-html-viewer-open";

// The sandbox has no allow-popups, because a popup's window handle (or its opener) would give author
// script a lasting two-way channel to an outside page. So new-tab links and window.open() are forwarded
// to the app, which opens the URL with no handle back to the frame. Tampering with this script can only
// break those links, never reopen the channel: without allow-popups the browser blocks the popup itself.
const POPUP_GUARD = `<script>(() => {
  const request = (url) => {
    try {
      parent.postMessage({ type: "${OPEN_LINK_MESSAGE}", url: new URL(String(url), document.baseURI).href }, "*");
    } catch {}
  };
  window.open = (url) => {
    if (url !== undefined && String(url) !== "") request(url);
    return null;
  };
  const opensNewTab = (event, link) => {
    if (event.type === "auxclick") return event.button === 1;
    if (event.ctrlKey || event.metaKey || event.shiftKey) return true;
    const target = link.getAttribute("target") ?? document.querySelector("base[target]")?.getAttribute("target") ?? "";
    return !["", "_self"].includes(target.trim().toLowerCase());
  };
  const onClick = (event) => {
    const link = event.composedPath().find((node) => node instanceof Element && node.matches("a, area"));
    const href = link && (link.getAttribute("href") ?? link.getAttributeNS("http://www.w3.org/1999/xlink", "href"));
    if (!href || !opensNewTab(event, link)) return;
    setTimeout(() => {
      if (!event.defaultPrevented) request(href);
    });
  };
  window.addEventListener("click", onClick, true);
  window.addEventListener("auxclick", onClick, true);
})();</script>`;

export const HTML_VIEWER_PREAMBLE = `<meta http-equiv="Content-Security-Policy" content="${HTML_VIEWER_CSP}">${WEBRTC_GUARD}${POPUP_GUARD}`;

// Only HTML's own whitespace (not JS \s, which also matches NBSP and other characters the parser treats
// as text) may precede the doctype. Anything else, comments included, gets the meta tag in front of it:
// that costs standards mode for the rare file with a comment before its doctype, but the tag always
// lands in <head>, where a meta CSP must be to apply.
const LEADING_DOCTYPE = /^[\t\n\f\r ]*<!doctype[^>]*>/i;

// Inject the CSP (and the guards) first so they apply before any author markup is parsed. A meta CSP
// can only be tightened by later policies, never relaxed, so author-supplied CSP tags can't undo it.
// A leading BOM is dropped: in a srcdoc string it is a text character, which would open <body>.
export function withContentSecurityPolicy(html: string): string {
  const source = html.startsWith("\uFEFF") ? html.slice(1) : html;
  const doctype = LEADING_DOCTYPE.exec(source)?.[0] ?? "";
  return `${doctype}${HTML_VIEWER_PREAMBLE}${source.slice(doctype.length)}`;
}

interface SecureHtmlViewerProps {
  htmlContent?: string | null;
}

export function SecureHtmlViewer({ htmlContent }: SecureHtmlViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Bundles run to hundreds of MB, so wrap each document once rather than on every render.
  const srcDoc = useMemo(() => (htmlContent ? withContentSecurityPolicy(htmlContent) : ""), [htmlContent]);

  useEffect(() => {
    let lastOpenedAt = 0;
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const url = linkToOpen(event.data);
      // Browser popup blockers differ (WebKit lets one click open many tabs), so require a fresh user
      // gesture and open at most one tab per gesture window. Browsers without the User Activation API
      // (Safari before 16.4) fall back to their own popup blocker plus the rate limit.
      const gestureActive = navigator.userActivation?.isActive ?? true;
      if (!url || !gestureActive || Date.now() - lastOpenedAt < MIN_MS_BETWEEN_OPENS) return;
      lastOpenedAt = Date.now();
      window.open(url, "_blank", "noopener,noreferrer");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!htmlContent) {
    return null;
  }

  return (
    <div className="h-full w-full">
      <iframe
        ref={iframeRef}
        sandbox="allow-scripts allow-forms allow-modals"
        className="h-full w-full border-0"
        title="HTML Content Viewer"
        srcDoc={srcDoc}
        // CSP Embedded Enforcement (Chromium): a second, frame-level copy of the policy.
        {...{ csp: HTML_VIEWER_CSP }}
      />
    </div>
  );
}

const MIN_MS_BETWEEN_OPENS = 1_000;

function linkToOpen(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const { type, url } = data as { type?: unknown; url?: unknown };
  if (type !== OPEN_LINK_MESSAGE || typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : null;
  } catch {
    return null;
  }
}
